import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import { Typography } from '@components/Typography';
import { MessageStatus, MessageType, SenderType } from '@constants/inbox';
import { CHANNEL, STATUS } from '@constants/channels';
import { useTranslation } from '@hooks';
import { getFullName } from '@helpers/userName';
import { handleApiError } from '@helpers/api';
import { inboxService } from '@services';
import MessageContentWrapper from './MessageContentWrapper';

const Container = styled('div', {
    shouldForwardProp: prop => prop !== 'type',
})(({ type }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 4,
    maxWidth: 300,
    alignItems: type === MessageType.Outgoing ? 'flex-end' : 'flex-start',
    alignSelf: type === MessageType.Outgoing ? 'flex-end' : 'flex-start',
    '@media (max-width: 768px)': {},
}));

const TextLink = styled(Typography)({
    color: 'rgba(0, 0, 0, 0.6)',
    marginLeft: '4px',
    cursor: 'pointer',
    '&:hover': {
        color: 'rgba(0, 0, 0, 0.8)',
    },
});

const messageIconStyles = {
    marginLeft: '4px',
    maxWidth: '12px',
    maxHeight: '14px',
};
const SentIcon = styled(DoneIcon)(() => ({ ...messageIconStyles, color: 'rgba(0, 0, 0, 0.38)' }));
const DeliveredIcon = styled(DoneAllIcon)(() => ({ ...messageIconStyles, color: 'rgba(0, 0, 0, 0.38)' }));
const FailedIcon = styled(ErrorIcon)(() => ({ ...messageIconStyles, color: 'rgba(0, 0, 0, 0.6)' }));
const ReadIcon = styled(DoneAllIcon)(() => ({ ...messageIconStyles, color: '#1F4C5C' }));

const Message = ({ data, users, locations, assignedLocation, channel, channelStatus }) => {
    const [resendDisabled, setResendDisabled] = useState(false);
    const { t } = useTranslation();

    const isOutgoing = data.type === MessageType.Outgoing;

    const senderName = useMemo(() => {
        const user = users.find(user => user.id === data.senderId);
        return getFullName(user?.firstName, user?.lastName);
    }, [data.senderId, users]);

    const internalName = useMemo(() => {
        return locations.find(l => l.companyId === assignedLocation)?.internalName;
    }, [assignedLocation, locations]);

    const onClickResendMessage = async () => {
        try {
            setResendDisabled(true);
            await inboxService.resendMessage(data.id);
        } catch (error) {
            handleApiError(error);
        } finally {
            setResendDisabled(false);
        }
    };

    return (
        <Container type={data.type}>
            {isOutgoing ? (
                <Typography
                    variant="caption"
                    sx={{ color: 'rgba(0, 0, 0, 0.6)', textAlign: 'right', marginBottom: '4px' }}
                >
                    {data.senderType === SenderType.User ? senderName : 'Pluspoint (Bot)'}
                </Typography>
            ) : null}
            {!isOutgoing && channel !== CHANNEL.FACEBOOK && channel !== CHANNEL.INSTAGRAM && assignedLocation ? (
                <Typography
                    variant="caption"
                    sx={{ color: 'rgba(0, 0, 0, 0.6)', textAlign: 'left', marginBottom: '4px' }}
                >
                    {internalName}
                </Typography>
            ) : null}
            {data.children.length === 1 ? (
                <MessageContentWrapper
                    data={data.children[0]}
                    type={data.type}
                    isLast
                    users={users}
                    locations={locations}
                    assignedLocation={assignedLocation}
                    isOutgoing={isOutgoing}
                    messageStatus={data.status}
                />
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isOutgoing ? 'flex-end' : 'flex-start',
                    }}
                >
                    {data.children.map((item, index) => (
                        <MessageContentWrapper
                            key={index}
                            data={item}
                            multiple
                            isLast={index === data.children.length - 1}
                            type={data.type}
                            users={users}
                            locations={locations}
                            assignedLocation={assignedLocation}
                            isOutgoing={isOutgoing}
                            messageStatus={data.status}
                        />
                    ))}
                </div>
            )}
            <div
                style={{
                    display: 'flex',
                    justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                    alignItems: 'center',
                    marginTop: 4,
                }}
            >
                <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                    {format(new Date(data.createdAt), 'dd/MM • HH:mm')}
                </Typography>
                {isOutgoing && data.status === MessageStatus.Failed ? (
                    <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', marginLeft: '4px' }}>
                        {t('common.failed')}
                    </Typography>
                ) : null}
                {isOutgoing && data.status === MessageStatus.Sent ? <SentIcon /> : null}
                {isOutgoing && data.status === MessageStatus.Delivered ? <DeliveredIcon /> : null}
                {isOutgoing && data.status === MessageStatus.Failed ? <FailedIcon /> : null}
                {isOutgoing && data.status === MessageStatus.Read ? <ReadIcon /> : null}
                {!resendDisabled &&
                isOutgoing &&
                data.status === MessageStatus.Failed &&
                channelStatus !== STATUS.DISCONNECTED ? (
                    <TextLink variant="caption" onClick={onClickResendMessage}>
                        {t('InboxPage.resend')}
                    </TextLink>
                ) : null}
            </div>
        </Container>
    );
};

export default Message;
